import { makeAutoObservable, runInAction, toJS } from "mobx";
import { esgService } from "../../services";
import { esgReportBuilderStore } from "./esg-report-builder.store";
import { esgDisclosureSelectionStore } from "./esg-disclosure-selection.store";
import { showToast } from "@/lib/utils/toast";
import {
  FIELD_LABELS,
  REQUIRED_FIELDS,
} from "../../constants/data-entry.constant";
import { promiseWithMinDelay } from "@/lib/utils/promiseWithMinDelay";

function getDisclosureInfo(code) {
  if (esgDisclosureSelectionStore.isUniversal) {
    const mm = esgDisclosureSelectionStore.metricMappings.find(
      (m) => m.masterMetricId === code,
    );
    if (mm) return { inputType: mm.inputType };
  }

  const all = [
    ...esgDisclosureSelectionStore.availableDisclosures,
    ...esgDisclosureSelectionStore.customDisclosures,
  ];
  const matching = all.filter((d) => d.code === code);

  if (matching.length > 1) {
    return {
      inputType: "subEntries",
      subDisclosures: [...matching].sort(
        (a, b) => (a.subEntryIndex ?? 0) - (b.subEntryIndex ?? 0),
      ),
    };
  }
  return { inputType: matching[0]?.inputType };
}

function isEntryComplete(entry, inputType, subDisclosures) {
  if (subDisclosures && subDisclosures.length > 0) {
    return subDisclosures.every((subDisc, i) => {
      const subEntry = entry.subEntries?.[i];
      if (!subEntry) return false;
      return isEntryComplete(subEntry, subDisc.inputType);
    });
  }
  const fields = REQUIRED_FIELDS[inputType] || ["value"];
  return fields.every((field) => entry[field] != null && entry[field] !== "");
}

function validateEntryFields(entry, inputType, subDisclosures) {
  if (subDisclosures && subDisclosures.length > 0) {
    const errors = {};
    subDisclosures.forEach((subDisc, i) => {
      const subErrors = validateEntryFields(
        entry.subEntries?.[i] || {},
        subDisc.inputType,
      );
      if (Object.keys(subErrors).length > 0) {
        errors[i] = subErrors;
      }
    });
    return errors;
  }

  const fields = REQUIRED_FIELDS[inputType] || ["value"];
  const errors = {};
  fields.forEach((field) => {
    if (entry[field] == null || entry[field] === "") {
      errors[field] = `${FIELD_LABELS[field] || field} is required`;
    }
  });
  return errors;
}

class ESGDataEntryStore {
  activeCategory = null;
  entriesByCode = new Map();
  errors = new Map();
  currentDisclosureCode = null;
  isDirty = false;
  isSaving = false;
  isUploading = false;
  lastSaved = null;
  autoSaveTimer = null;

  // Prefill state
  prefillSuggestions = [];
  loadingPrefill = false;
  showPrefillBanner = false;
  showPrefillModal = false;

  constructor() {
    makeAutoObservable(this, {
      autoSaveTimer: false,
      createDefaultEntry: false,
      isDisclosureCompleted: false,
      serializeDisclosureData: false,
      completedCountByCategory: false,
    });
  }

  // Computed
  get currentEntries() {
    return this.entriesByCode.get(this.currentDisclosureCode) || [];
  }

  get completedCount() {
    const allCodes = Object.values(
      esgDisclosureSelectionStore.selectedCodesByCategory,
    ).flat();
    return allCodes.filter((code) => this.isDisclosureCompleted(code)).length;
  }

  get progressPercentage() {
    const total = Object.values(
      esgDisclosureSelectionStore.selectedCodesByCategory,
    ).flat().length;
    if (total === 0) return 0;
    return Math.round((this.completedCount / total) * 100);
  }

  // Actions
  setActiveCategory = (category) => (this.activeCategory = category);

  createDefaultEntry = () => ({
    entryId: crypto.randomUUID(),
    label: "",
    value: "",
    unit: "",
    valueUnit: "",
    location: "",
    category: "",
    denominator: null,
    subEntries: [],
    proofs: [],
  });

  isDisclosureCompleted = (code) => {
    const entries = this.entriesByCode.get(code);
    if (!entries || entries.length === 0) return false;

    const { inputType, subDisclosures } = getDisclosureInfo(code);
    return entries.every((entry) =>
      isEntryComplete(entry, inputType, subDisclosures),
    );
  };

  completedCountByCategory = (category) => {
    const codes =
      esgDisclosureSelectionStore.selectedCodesByCategory[category] || [];
    return codes.filter((code) => this.isDisclosureCompleted(code)).length;
  };

  serializeDisclosureData = () => {
    const result = [];
    this.entriesByCode.forEach((entries, code) => {
      if (entries && entries.length > 0) {
        result.push({ disclosureCode: code, entries });
      }
    });
    return result;
  };

  setCurrentDisclosure = (code) => {
    this.currentDisclosureCode = code;
    if (code && !this.entriesByCode.has(code)) {
      this.entriesByCode.set(code, [this.createDefaultEntry()]);
    }
  };

  // Data Entry
  updateEntry = (code, entryIndex, field, value) => {
    if (!this.entriesByCode.has(code)) {
      this.entriesByCode.set(code, [this.createDefaultEntry()]);
    }

    const entries = this.entriesByCode.get(code);
    if (!entries[entryIndex]) {
      entries[entryIndex] = this.createDefaultEntry();
    }

    entries[entryIndex][field] = value;
    this.isDirty = true;
    this.validateEntry(code, entryIndex);
    this.queueAutoSave();
  };

  addEntry = (code) => {
    if (!this.entriesByCode.has(code)) {
      this.entriesByCode.set(code, []);
    }
    this.entriesByCode.get(code).push(this.createDefaultEntry());
    this.isDirty = true;
  };

  removeEntry = (code, entryIndex) => {
    const entries = this.entriesByCode.get(code);
    if (entries) {
      entries.splice(entryIndex, 1);
      this.isDirty = true;
    }
  };

  validateEntry = (code, entryIndex) => {
    const entries = this.entriesByCode.get(code);
    if (!entries || !entries[entryIndex]) return false;

    const { inputType, subDisclosures } = getDisclosureInfo(code);
    const errors = validateEntryFields(
      entries[entryIndex],
      inputType,
      subDisclosures,
    );

    if (Object.keys(errors).length > 0) {
      this.errors.set(`${code}_${entryIndex}`, errors);
      return false;
    }
    this.errors.delete(`${code}_${entryIndex}`);
    return true;
  };

  // Evidence File
  uploadFile = async (file) => {
    runInAction(() => {
      this.isUploading = true;
    });

    const reportId = esgReportBuilderStore.reportId;
    if (!reportId) return;
    const formData = new FormData();
    formData.append("file", file);

    try {
      const uploadPromise = esgService.uploadFile(reportId, formData);

      showToast.promise(uploadPromise, {
        loading: "Uploading",
        success: "Successfully uploaded",
        error: "Failed to upload",
      });

      const { file: proof } = await promiseWithMinDelay(uploadPromise);
      return proof;
    } finally {
      runInAction(() => {
        this.isUploading = false;
      });
    }
  };

  downloadFile = async (proof) => {
    const reportId = esgReportBuilderStore.reportId;
    if (!reportId) return;
    const downloadPromise = esgService.downloadFile(reportId, proof);

    showToast.promise(downloadPromise, {
      loading: "Downloading",
      success: "Successfully downloaded",
      error: "Failed to download",
    });

    await promiseWithMinDelay(downloadPromise);
  };

  removeFile = async (fileId) => {
    const reportId = esgReportBuilderStore.reportId;
    if (!reportId) return;
    const deletePromise = esgService.deleteFile(reportId, fileId);

    showToast.promise(deletePromise, {
      loading: "Removing file",
      success: "Successfully removed",
      error: "Failed to remove file",
    });

    await promiseWithMinDelay(deletePromise);
  };

  // Auto-save
  queueAutoSave = () => {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
    }
    this.autoSaveTimer = setTimeout(() => {
      this.save();
    }, 2000);
  };

  cancelAutoSave = () => {
    if (this.autoSaveTimer) {
      clearTimeout(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  };

  save = async () => {
    if (!this.isDirty) return;

    const reportId = esgReportBuilderStore.reportId;
    if (!reportId) return;

    runInAction(() => {
      this.isSaving = true;
    });

    try {
      const payload = esgReportBuilderStore.buildReportPayload();
      await promiseWithMinDelay(esgService.updateReport(reportId, payload));

      runInAction(() => {
        this.isDirty = false;
        this.lastSaved = new Date();
      });
    } finally {
      runInAction(() => {
        this.isSaving = false;
      });
    }
  };

  loadData = (data) => {
    runInAction(() => {
      this.entriesByCode.clear();
      this.activeCategory = null;

      data.disclosureData?.forEach((item) => {
        this.entriesByCode.set(item.disclosureCode, item.entries || []);
      });

      this.isDirty = false;
    });
  };

  // Prefill methods
  loadPrefillSuggestions = async () => {
    const { reportId, framework, reportYear } = esgReportBuilderStore;
    const { selectedCodes } = esgDisclosureSelectionStore;

    if (!reportId || reportId === "new" || !selectedCodes.length) return;

    runInAction(() => {
      this.loadingPrefill = true;
    });

    try {
      const response = await esgService.getPrefillSuggestions({
        reportId,
        framework,
        reportYear,
        selectedDisclosures: JSON.stringify(selectedCodes),
      });

      runInAction(() => {
        // Filter out matches where target disclosure already has completed data
        const filteredMatches = (response.matches || []).filter((match) => {
          return !this.isDisclosureCompleted(match.targetCode);
        });

        this.prefillSuggestions = filteredMatches;
        this.showPrefillBanner = filteredMatches.length > 0;
        this.loadingPrefill = false;
      });
    } catch (error) {
      console.error("Failed to load prefill suggestions:", error);
      runInAction(() => {
        this.loadingPrefill = false;
      });
    }
  };

  applyPrefillSuggestions = () => {
    this.prefillSuggestions.forEach((match) => {
      // Only apply if has entries to apply
      if (match.entries?.length) {
        const newEntries = match.entries.map((entry) => ({
          ...entry,
          entryId: crypto.randomUUID(), // Generate new IDs
        }));
        this.entriesByCode.set(match.targetCode, newEntries);
      }
    });

    this.isDirty = true;
    this.showPrefillBanner = false;
    this.showPrefillModal = false;
    showToast.success(
      `Applied ${this.prefillSuggestions.length} prefilled values`,
    );
  };

  setShowPrefillBanner = (show) => (this.showPrefillBanner = show);
  setShowPrefillModal = (show) => (this.showPrefillModal = show);

  dismissPrefillBanner = () => {
    this.showPrefillBanner = false;
  };

  resetDataEntry = () => {
    runInAction(() => {
      this.entriesByCode.clear();
      this.errors.clear();
      this.currentDisclosureCode = null;
      this.activeCategory = null;
      this.isDirty = false;
      this.lastSaved = null;
      this.prefillSuggestions = [];
      this.showPrefillBanner = false;
      this.showPrefillModal = false;
    });
  };
}

export const esgDataEntryStore = new ESGDataEntryStore();
